import type { KyInstance } from "ky";
import { toSearchParams, type PaginatedRequest, type PaginatedResponse } from "./pagination.js";
import type { AuthResponse, GoogleAuthUrlResponse, GoogleExchangeRequest, InvitableRole } from "./index.js";

export interface CreateInvitationRequest {
  email: string;
  role: InvitableRole;
}

export interface Invitation {
  id: string;
  email: string;
  expiresAt: string;
}

export type InvitationStatus = "pending" | "accepted" | "expired" | "revoked";

export type InvitationSortKey = "email" | "status" | "createdAt";

export interface InvitationListItem {
  id: string;
  email: string;
  status: InvitationStatus;
  role: InvitableRole;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
}

export interface ListInvitationsRequest extends PaginatedRequest {
  sortBy?: InvitationSortKey;
  status?: InvitationStatus;
  role?: InvitableRole;
}

export interface InvitationPreview {
  email: string;
  role: InvitableRole;
  expiresAt: string;
}

export interface AcceptInvitationRequest {
  token: string;
  password: string;
}

export class InvitationApi {
  constructor(private readonly http: KyInstance) { }

  create = (body: CreateInvitationRequest): Promise<Invitation> =>
    this.http.post("invitations", { json: body }).json<Invitation>();

  list = (params: ListInvitationsRequest = {}): Promise<PaginatedResponse<InvitationListItem>> =>
    this.http
      .get("invitations", { searchParams: toSearchParams({ ...params }) })
      .json<PaginatedResponse<InvitationListItem>>();

  resend = (id: string): Promise<Invitation> =>
    this.http.post(`invitations/${encodeURIComponent(id)}/resend`).json<Invitation>();

  preview = (token: string): Promise<InvitationPreview> =>
    this.http
      .get("auth/invitations/accept", { searchParams: { token } })
      .json<InvitationPreview>();

  accept = (body: AcceptInvitationRequest): Promise<AuthResponse> =>
    this.http.post("auth/invitations/accept", { json: body }).json<AuthResponse>();

  googleAuthUrl = (token: string): Promise<GoogleAuthUrlResponse> =>
    this.http
      .get("auth/invitations/accept/google", { searchParams: { token } })
      .json<GoogleAuthUrlResponse>();

  acceptWithGoogle = (body: GoogleExchangeRequest): Promise<AuthResponse> =>
    this.http
      .post("auth/invitations/accept/google", { json: body })
      .json<AuthResponse>();
}
